import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { IconMail, IconLock, IconAlertCircle, IconArrowRight, IconShieldCheck } from '@tabler/icons-react';
import { Button } from '../components/ui';
import CodedPill from '../components/CodedPill';
import { useAuth } from '../hooks/useAuth';
import { useRole } from '../hooks/useRole';

/**
 * Admin sign-in — back-office (email + password) for MANAGER+.
 *
 * Cashiers and supervisors use the till PIN at /login. The page mirrors
 * the /login design language exactly: cream surface, CODED brand mark,
 * design tokens (bg-light / text-heading / text-primary), the standard
 * Button component, and CodedPill in the footer. The only visual cue
 * that this is back-office is the small "Back office" eyebrow + a
 * subtle shield chip — no dark theme, no neon, no marketing-style
 * gradients.
 */

export default function AdminLoginPage() {
  const { signInWithPassword, isAuthenticated, loading } = useAuth();
  const { isManagerPlus } = useRole();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && isAuthenticated && isManagerPlus) {
    return <Navigate to="/admin" replace />;
  }
  if (!loading && isAuthenticated && !isManagerPlus) {
    return <Navigate to="/pos" replace />;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await signInWithPassword(email.trim(), password);
      if (!result) throw new Error('Sign-in failed');
      navigate('/admin', { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !submitting && email.trim().length > 0 && password.length > 0;

  return (
    <div className="min-h-screen bg-light flex flex-col">
      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-[420px] flex flex-col items-center">
          {/* Brand — same lockup as /login */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <img
                src="/icons/icon-96.png"
                alt="CODED"
                className="w-11 h-11 rounded-xl flex-shrink-0 shadow-sm"
              />
              <h1 className="text-22 font-bold text-heading tracking-tight leading-none">
                CODED. POS
              </h1>
            </div>
            <p className="text-13 text-body">HL Coffee Shop · Point of Sale</p>
          </div>

          {/* Eyebrow chip — the only visual that distinguishes admin from /login */}
          <div className="inline-flex items-center gap-1.5 px-2.5 h-6 rounded-md bg-primary-soft text-primary mb-3">
            <IconShieldCheck size={13} stroke={2.5} />
            <span className="text-11 font-bold uppercase tracking-widest">Back office</span>
          </div>
          <p className="text-14 text-body text-center mb-6 max-w-[320px] leading-snug">
            Managers, admins and owners sign in here. Cashiers and supervisors use the till PIN at <span className="font-semibold text-heading">/login</span>.
          </p>

          {/* Sign-in card */}
          <form
            onSubmit={submit}
            className="w-full rounded-xl bg-surface border border-border shadow-sm p-6 space-y-4"
          >
            <div>
              <label htmlFor="admin-email" className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <IconMail size={16} stroke={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  id="admin-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  placeholder="you@habbyandlace.com"
                  className="w-full h-12 pl-10 pr-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading placeholder-gray-400 disabled:opacity-50 transition-colors"
                />
              </div>
            </div>

            <div>
              <label htmlFor="admin-password" className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <IconLock size={16} stroke={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  placeholder="••••••••"
                  className="w-full h-12 pl-10 pr-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading placeholder-gray-400 disabled:opacity-50 transition-colors tracking-widest"
                  onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) submit(e as unknown as React.FormEvent); }}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2">
                <IconAlertCircle size={16} className="text-danger flex-shrink-0 mt-0.5" />
                <p className="text-13 font-semibold text-danger">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="xl"
              fullWidth
              disabled={!canSubmit}
              trailingIcon={!submitting ? <IconArrowRight size={18} stroke={2.5} /> : undefined}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <p className="text-13 text-body mt-5">
            On the till floor?{' '}
            <a href="/login" className="text-primary font-bold hover:underline">
              PIN sign-in →
            </a>
          </p>
        </div>
      </main>

      <footer className="pb-8 pt-4 flex flex-col items-center gap-2.5">
        <span className="text-xs text-ink-tertiary">This platform runs on the</span>
        <CodedPill size="sm" />
      </footer>
    </div>
  );
}
